import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_SERVER_ADDRESS } from '../../environments/environment';
import { TranslateConfigService } from './translate-config.service';

export interface BilleteData {
  expedicion_id: number;
  expediciones: number;
  hora_salida: string;
  precio: number;
  sentido: string;
  origen_id: number;
  destino_id: number;
  origen_nombre: string;
  destino_nombre: string;
  fecha: string;
  tarifa_id: number;
  tarifa_name: string;
  por: string;
}

export interface CompraData {
  origen: string;
  usuario_id: number;
  cantidad: number;
  tarifaSeleccionada: any;
  importe: number;
  billetes: BilleteData[];
  // Campos adicionales para usuarios no autenticados
  user_nombre?: string;
  user_email?: string;
  accept_tos?: boolean;
  accept_privacy?: boolean;
  accept_marketing?: boolean;
  lang?: string;
}

export interface AnularCompraData {
  compra_id: number;
}

export interface BilleteEstado {
  estado: 'activo' | 'caducado' | 'pendiente pago' | 'usado' | 'anulado';
}

export interface CompraConBilletes {
  id: number;
  billetes: BilleteEstado[];
}

@Injectable({
  providedIn: 'root'
})
export class CompraService {

  private apiUrl = `${API_SERVER_ADDRESS}`;

  constructor(
    private http: HttpClient,
    private translateConfig: TranslateConfigService
  ) { }

  /**
   * Obtener el idioma seleccionado en la app o 'es' por defecto
   */
  private getLanguage(): string {
    const lang = this.translateConfig.currentLang.value;
    return typeof lang === 'string' && lang ? lang : 'es';
  }

  /**
   * Validar si una expedición es vendible
   * @param expedicion - Datos de la expedición
   */
  isExpedicionVendible(expedicion: any): boolean {
    return expedicion.precios &&
      expedicion.precios.some((precio: any) =>
        precio.tarifa && precio.tarifa.es_vendible === true
      );
  }

  /**
   * Obtener tarifas vendibles de una expedición
   * @param expedicion - Datos de la expedición
   */
  getTarifasVendibles(expedicion: any): any[] {
    if (!expedicion.precios) {
      return [];
    }

    return expedicion.precios.filter((precio: any) =>
      precio.tarifa && precio.tarifa.es_vendible === true
    );
  }

  /**
   * Obtener tarifa por defecto (Normal, idFare 100) si es vendible
   * @param expedicion - Datos de la expedición
   */
  getTarifaDefecto(expedicion: any): any | null {
    const tarifasVendibles = this.getTarifasVendibles(expedicion);

    // Buscar tarifa Normal (idFare 100)
    const tarifaNormal = tarifasVendibles.find(precio =>
      precio.tarifa?.nom_web?.toLowerCase() === 'normal'
    );
    if (tarifaNormal) {
      return tarifaNormal;
    }

    // Si no hay tarifa Normal, devolver la primera vendible
    return tarifasVendibles.length > 0 ? tarifasVendibles[0] : null;
  }

  /**
   * Obtener precio para mostrar en botón
   * @param expedicion - Datos de la expedición
   */
  getPrecioMostrar(expedicion: any): string {
    if (!expedicion.precios) {
      return (expedicion.tarifa_basica / 100).toFixed(2);
    }

    const tarifaVendible = expedicion.precios.find((precio: any) =>
      precio.tarifa && precio.tarifa.es_vendible === true
    );

    if (tarifaVendible) {
      return tarifaVendible.price.toFixed(2);
    }

    return (expedicion.tarifa_basica / 100).toFixed(2);
  }


  /**
   * Listar compras del usuario
   */
  getCompras(): Observable<any> {
    return this.http.get(`${this.apiUrl}/v1/compras`).pipe(
      map(results => results['compras'])
    );
  }

  /**
   * Listar compras activas del usuario
   */
  getComprasActivas(): Observable<any> {
    let params = new HttpParams();
    params = params.set('finders', 'conTodosBilletesActivos');
    params = params.set('contain', 'Billetes');  // Añadir contain para traer billetes

    return this.http.get(`${this.apiUrl}/v1/compras`, { params });
  }
  /**
   * Procesar carrito de compra para TPV
   * @param compraData - Datos de la compra
   */
  crearCompra(compraData: CompraData): Observable<any> {
    const lang = this.getLanguage();
    const payload = { ...compraData, lang };
    return this.http.post(`${this.apiUrl}/v1/compras/procesar-carrito`, payload);
  }

  /**
   * Procesar carrito (alias para compatibilidad)
   * @param postData - Datos del carrito
   */
  procesarCarrito(postData): Observable<any> {
    const lang = this.getLanguage();
    const payload = { ...postData, lang };
    return this.http.post(`${this.apiUrl}/v1/compras/procesar-carrito`, payload).pipe(
      map(results => results)
    );
  }

  /**
   * Obtener compra específica
   * @param compraId - ID de la compra
   */
  getCompra(compraId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/v1/compras/${compraId}`).pipe(
      map(results => results['compra'])
    );
  }

  /**
   * Anular compra
   * @param compraId - ID de la compra
   */
  anularCompra(compraId: number): Observable<any> {
    return this.http.patch(`${this.apiUrl}/v1/compras/${compraId}/anular`, {'compra_id': compraId}).pipe(
      map(results => results)
    );
  }



  /**
   * Verificar si una compra es anulable basado en la lógica del servidor
   * @param compra - Datos de la compra con billetes
   */
  isCompraAnulable(compra: CompraConBilletes): { anulable: boolean; mensaje?: string } {
    // Verificar que la compra existe
    if (!compra) {
      return {
        anulable: false,
        mensaje: 'No existe la compra'
      };
    }

    // Verificar que tiene billetes
    if (!compra.billetes || compra.billetes.length === 0) {
      return {
        anulable: false,
        mensaje: 'No hay billetes que anular en esta compra'
      };
    }

    // Contar estados de billetes
    const estados = this.contarEstadosBilletes(compra.billetes);

    // Verificar que no hay billetes en estados no anulables
    if (estados['caducado'] || estados['pendiente pago'] || estados['usado'] || estados['anulado']) {
      return {
        anulable: false,
        mensaje: 'No se puede anular la compra, hay billetes ya usados o anulados.'
      };
    }

    return { anulable: true };
  }

  /**
   * Contar billetes por estado
   * @param billetes - Array de billetes
   */
  private contarEstadosBilletes(billetes: BilleteEstado[]): Record<string, number> {
    const estados: Record<string, number> = {};

    billetes.forEach(billete => {
      const estado = billete.estado;
      estados[estado] = (estados[estado] || 0) + 1;
    });

    return estados;
  }

  /**
   * Verificar si todos los billetes están activos
   * @param billetes - Array de billetes
   */
  todosBilletesActivos(billetes: BilleteEstado[]): boolean {
    return billetes.every(billete => billete.estado === 'activo');
  }
}